import { Auth } from 'aws-amplify';
import { Effect, Option, pipe } from 'effect';
import Cookies from 'js-cookie';
import getDomain from 'helpers/getDomain';
import isURL from 'validator/lib/isURL';
import { getRuntimeConfig } from 'helpers/runtimeConfig';
import Api from 'services/api';

const storageBackurlKey = 'BACKURL';
const storage = window.sessionStorage;
let redirectInProgress = false;

export const getToken = async () => {
  const session = await Auth.currentSession();
  return session.getAccessToken().getJwtToken();
};

export const getAppRedirectData = async (backUrl: string) => {
  // passing null to { urlParams: { backUrl: null } } results in error
  const options = pipe(
    backUrl,
    Option.fromNullable,
    Option.map((backUrl) => ({ urlParams: { backUrl } })),
    Option.getOrElse(() => ({})),
  );

  return Api.use((service) => service.postAuth(options)).pipe(
    Effect.flatMap((response) => {
      return response.json;
    }),
    Effect.catchTag(
      'ValidatedResponseError',
      Effect.fn(function* (error) {
        const response = error.validatedBody;
        console.error('PostLogin api request failed:', response);

        // For non-IP-restriction validation errors: sign out
        // IP restriction signout is handled by Exit.match handler
        if (!(error.status === 403 && response.type === 'invalid_remote_address')) {
          yield* Effect.promise(() => Auth.signOut()).pipe(
            Effect.catchAll(() => Effect.void),
            Effect.asVoid,
          );
        }

        return yield* Effect.fail(error);
      }),
    ),
    Effect.tap((response) => {
      console.log('PostAuth response', response);
      console.log('Redirecting user to', response.redirectUrl);
      handleDeliveryMethod(response.isBeta);
      handleAPIEndpoint(response.apiUrl);
    }),
    Effect.provide(Api.Default),
    Effect.runPromiseExit,
  );
};

export const preForgotPasswordHook = async (email: string) => {
  const params = { email };
  const url = `${getRuntimeConfig().env.REACT_APP_API_BASEURL}/auth/preForgotPassword?${new URLSearchParams(params).toString()}`;
  return fetch(url, {
    method: 'GET',
  }).catch((err) => console.error(err));
};

export const handleDeliveryMethod = (canary: boolean) => {
  const cookieName = getRuntimeConfig().env.REACT_APP_CANARY_DELIVERY_COOKIE_NAME;
  const cookieValue = getRuntimeConfig().env.REACT_APP_CANARY_DELIVERY_COOKIE_VALUE;
  if (!cookieName || !cookieValue) {
    return;
  }
  const deliveryCookieOptions = {
    expires: parseInt(getRuntimeConfig().env.REACT_APP_CANARY_DELIVERY_COOKIE_EXPIRATION || '365', 10),
    domain: getDomain(),
  };
  canary
    ? Cookies.set(cookieName, cookieValue, deliveryCookieOptions)
    : Cookies.remove(cookieName, deliveryCookieOptions);
};

export const handleAPIEndpoint = (value: string) => {
  const cookieName = getRuntimeConfig().env.REACT_APP_API_ENDPOINT_COOKIE_NAME;
  if (!cookieName) {
    return;
  }
  const endpointCookieOptions = {
    expires: parseInt(getRuntimeConfig().env.REACT_APP_API_ENDPOINT_COOKIE_EXPIRATION || '60', 10),
    domain: getDomain(),
  };

  value ? Cookies.set(cookieName, value, endpointCookieOptions) : Cookies.remove(cookieName, endpointCookieOptions);
};

export const isValidURL = (url: string | null) => {
  const isValid =
    url &&
    isURL(url, {
      protocols: ['https'],
      require_protocol: true,
      require_host: true,
    });

  if (isValid) {
    const domains = getDomain() ? [getDomain()] : [];
    const host = process.env.REACT_APP_ENV === 'production' ? new URL(url).host : new URL(url).hostname;
    return domains.length ? domains.some((h) => host.endsWith(h)) : true;
  } else {
    if (typeof url === 'string') console.error('Invalid redirect URL: ', url);
  }
  return false;
};

const parseBackUrl = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const backurl = queryParams.get('backurl');
  return isValidURL(backurl) ? backurl : null;
};

export const persistBackUrl = () => {
  const backurl = parseBackUrl();
  if (backurl !== null) {
    // @to-do detect mobile and use sessionStorage instead for mobile
    storage.setItem(storageBackurlKey, backurl);
  }
};

export const getBackUrl = () => {
  const url = storage.getItem(storageBackurlKey);
  return isValidURL(url) ? url : null;
};

export const redirectToApp = (redirectUrl: string) => {
  if (redirectInProgress) {
    return;
  }
  redirectInProgress = true;

  if (redirectUrl) {
    storage.removeItem(storageBackurlKey);
    (window.location as any) = redirectUrl;
  }
};

export const getHostnameFromUrlString = (urlString: string) => {
  try {
    const url = new URL(urlString);

    return url.hostname;
  } catch (error) {
    console.error('Invalid URL:', error);
    return null;
  }
};
